import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from "react-router-dom";
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, addMonths, subMonths, addWeeks, subWeeks, isSameDay, isSameMonth } from 'date-fns';
import { it, enUS } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import { calendarApi, lessonsApi } from "@/api";
import Header from "@/components/Header";
import LessonModal from "@/components/LessonModal";

const CalendarScreen = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('month');
  const [lessons, setLessons] = useState({});
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedLesson, setSelectedLesson] = useState(null);

  const currentLocale = i18n.language?.startsWith('it') ? it : enUS;
  const dayNames = [
    t('calendar.dayNames.mon'),
    t('calendar.dayNames.tue'),
    t('calendar.dayNames.wed'),
    t('calendar.dayNames.thu'),
    t('calendar.dayNames.fri'),
    t('calendar.dayNames.sat'),
    t('calendar.dayNames.sun')
  ];

  const fetchData = useCallback(async () => {
    try {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() + 1;
      const day = currentDate.getDate();

      const response = viewMode === 'month'
        ? await calendarApi.getMonth(year, month)
        : await calendarApi.getWeek(year, month, day);

      setLessons(viewMode === 'month' ? response.data : response.data.lessons || {});
    } catch (error) {
      // Silently handle errors in production
    }
  }, [currentDate, viewMode]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleUpdateLesson = async (lessonId, data) => {
    await lessonsApi.update(lessonId, data);
    fetchData();
  };

  const handleDeleteLesson = async (lessonId) => {
    await lessonsApi.delete(lessonId);
    fetchData();
  };

  const handleCompleteLesson = async (lessonId) => {
    await lessonsApi.complete(lessonId);
    fetchData();
  };

  const handleCancelLesson = async (lessonId) => {
    await lessonsApi.cancel(lessonId);
    fetchData();
  };

  const navigatePrevious = () => {
    setCurrentDate(viewMode === 'month' ? subMonths(currentDate, 1) : subWeeks(currentDate, 1));
  };

  const navigateNext = () => {
    setCurrentDate(viewMode === 'month' ? addMonths(currentDate, 1) : addWeeks(currentDate, 1));
  };

  const renderMonthView = () => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const startDate = startOfWeek(monthStart, { weekStartsOn: 1 });
    const endDate = endOfWeek(monthEnd, { weekStartsOn: 1 });

    const days = [];
    let day = startDate;
    while (day <= endDate) {
      days.push(day);
      day = addDays(day, 1);
    }

    return (
      <div className="bg-white rounded-xl shadow-sm p-4">
        <div className="grid grid-cols-7 gap-1 mb-2">
          {dayNames.map(d => (
            <div key={d} className="text-center text-xs font-medium text-gray-500 py-2">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {days.map((d, i) => {
            const dateStr = format(d, 'yyyy-MM-dd');
            const dayLessons = lessons[dateStr] || [];
            const isCurrentMonth = isSameMonth(d, currentDate);
            const isToday = isSameDay(d, new Date());
            const isSelected = selectedDate && isSameDay(d, selectedDate);

            return (
              <button
                key={format(d, 'yyyy-MM-dd')}
                onClick={() => setSelectedDate(d)}
                className={`aspect-square flex flex-col items-center justify-center rounded-lg transition-colors ${
                  !isCurrentMonth ? 'text-gray-300' :
                  isSelected ? 'bg-indigo-600 text-white' :
                  isToday ? 'bg-indigo-100 text-indigo-700' :
                  'hover:bg-gray-100'
                }`}
              >
                <span className="text-sm">{format(d, 'd')}</span>
                {dayLessons.length > 0 && (
                  <span className={`w-5 h-5 rounded-full text-xs flex items-center justify-center mt-1 ${
                    isSelected ? 'bg-white text-indigo-600' : 'bg-indigo-600 text-white'
                  }`}>
                    {dayLessons.length}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const renderWeekView = () => {
    const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
    const days = [];
    for (let i = 0; i < 7; i++) {
      days.push(addDays(weekStart, i));
    }

    const timeSlots = [];
    for (let hour = 7; hour <= 21; hour++) {
      timeSlots.push(`${hour.toString().padStart(2, '0')}:00`);
    }

    return (
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="grid grid-cols-8 border-b">
          <div className="p-2 text-center text-xs text-gray-400">{t('calendar.hour')}</div>
          {days.map((d, i) => {
            const isToday = isSameDay(d, new Date());
            return (
              <div 
                key={format(d, 'yyyy-MM-dd')} 
                className={`p-2 text-center border-l ${isToday ? 'bg-indigo-50' : ''}`}
              >
                <div className="text-xs text-gray-500 capitalize">
                  {format(d, 'EEE', { locale: currentLocale })}
                </div>
                <div className={`text-sm font-bold ${isToday ? 'text-indigo-600' : 'text-gray-900'}`}>
                  {format(d, 'd')}
                </div>
              </div>
            );
          })}
        </div>

        <div className="max-h-96 overflow-y-auto">
          {timeSlots.map((time) => (
            <div key={time} className="grid grid-cols-8 border-b min-h-[50px]">
              <div className="p-1 text-xs text-gray-400 text-right pr-2 pt-1">
                {time}
              </div>
              {days.map((d, i) => {
                const dateStr = format(d, 'yyyy-MM-dd');
                const dayLessons = lessons[dateStr] || [];
                const hourLessons = dayLessons.filter(l => 
                  l.start_time && l.start_time.startsWith(time.split(':')[0])
                );

                return (
                  <div 
                    key={`${dateStr}-${time}`} 
                    className="border-l p-0.5 cursor-pointer hover:bg-gray-50"
                    onClick={(e) => {
                      if (e.target === e.currentTarget) {
                        setSelectedDate(d);
                        navigate(`/prenota?date=${dateStr}&time=${time}`);
                      }
                    }}
                  >
                    {hourLessons.map((lesson, idx) => (
                      <div 
                        key={idx}
                        className="bg-indigo-600 text-white text-xs p-1 rounded mb-0.5 truncate cursor-pointer hover:bg-indigo-700"
                        title={`${lesson.client_name} ${lesson.start_time}-${lesson.end_time}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedLesson(lesson);
                        }}
                      >
                        {lesson.client_name?.split(' ')[0] || 'Cliente'}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderSelectedDateLessons = () => {
    if (!selectedDate) return null;
    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    const dayLessons = lessons[dateStr] || [];

    return (
      <div className="mt-4">
        <div className="flex justify-between items-center mb-3">
          <h3 className="font-bold text-gray-900 capitalize">
            {format(selectedDate, 'EEEE d MMMM', { locale: currentLocale })}
          </h3>
          <button
            onClick={() => navigate(`/prenota?date=${dateStr}`)}
            className="w-8 h-8 bg-indigo-600 text-white rounded-full flex items-center justify-center"
          >
            +
          </button>
        </div>
        {dayLessons.length === 0 ? (
          <p className="text-center text-gray-500 py-4">{t('calendar.noLessons')}</p>
        ) : (
          <div className="space-y-2">
            {dayLessons.map((lesson) => (
              <div 
                key={lesson.id || `${lesson.date}-${lesson.start_time}`} 
                className="bg-white rounded-xl shadow-sm p-4 flex items-center gap-4 cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => setSelectedLesson(lesson)}
              >
                <div className="bg-indigo-100 text-indigo-700 px-3 py-2 rounded-lg text-sm font-medium">
                  {lesson.start_time} - {lesson.end_time}
                </div>
                <div className="flex-1">
                  <div className="font-semibold">{lesson.client_name}</div>
                  {lesson.notes && <div className="text-sm text-gray-500">{lesson.notes}</div>}
                </div>
                <span className="text-gray-400">&rarr;</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900 pb-20 md:pt-16">
      <Header 
        title={t('calendar.title')} 
        rightAction={
          <button 
            onClick={() => navigate('/prenota')}
            className="bg-white/20 px-3 py-1 rounded-lg text-sm"
          >
            + {t('calendar.newLesson')}
          </button>
        }
      />
      
      <div className="max-w-lg mx-auto p-4">
        <div className="flex items-center justify-between mb-4">
          <button onClick={navigatePrevious} className="p-2 hover:bg-gray-200 rounded-lg">
            &larr;
          </button>
          <span className="font-bold text-gray-900 capitalize">
            {format(currentDate, viewMode === 'month' ? 'MMMM yyyy' : "'Sett.' w, MMMM yyyy", { locale: currentLocale })}
          </span>
          <button onClick={navigateNext} className="p-2 hover:bg-gray-200 rounded-lg">
            &rarr;
          </button>
        </div>

        <div className="flex bg-gray-100 rounded-xl p-1 mb-4 shadow-sm">
          <button
            onClick={() => setViewMode('month')}
            className={`flex-1 py-3 rounded-lg text-sm font-semibold transition-all flex items-center justify-center gap-2 ${
              viewMode === 'month' 
                ? 'bg-indigo-600 text-white shadow-md' 
                : 'text-gray-600 hover:bg-gray-200'
            }`}
          >
            {t('calendar.monthView')}
          </button>
          <button
            onClick={() => setViewMode('week')}
            className={`flex-1 py-3 rounded-lg text-sm font-semibold transition-all flex items-center justify-center gap-2 ${
              viewMode === 'week' 
                ? 'bg-indigo-600 text-white shadow-md' 
                : 'text-gray-600 hover:bg-gray-200'
            }`}
          >
            {t('calendar.weekView')}
          </button>
        </div>

        <button 
          onClick={() => { setCurrentDate(new Date()); setSelectedDate(new Date()); }}
          className="mb-4 px-4 py-2 bg-indigo-100 text-indigo-700 rounded-lg text-sm font-medium"
        >
          {t('calendar.today')}
        </button>

        {viewMode === 'month' ? renderMonthView() : renderWeekView()}
        {viewMode === 'month' && renderSelectedDateLessons()}
      </div>

      {selectedLesson && (
        <LessonModal
          lesson={selectedLesson}
          onClose={() => setSelectedLesson(null)}
          onUpdate={handleUpdateLesson}
          onDelete={handleDeleteLesson}
          onComplete={handleCompleteLesson}
          onCancel={handleCancelLesson}
        />
      )}
    </div>
  );
};

export default CalendarScreen;
